import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Stethoscope, ArrowUpRight, Pencil, Trash2, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useConfirm } from "@/components/ConfirmDialog";

const defaultForm = { name: "", notes: "", headset_id: "" };

export default function Roster() {
  const [patients, setPatients] = useState([]);
  const [headsets, setHeadsets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const confirmDialog = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([api.get("/patients"), api.get("/headsets")]);
      setPatients(p.data);
      setHeadsets(h.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Source of truth: headset.patient_id (the therapist's My-Headsets flow updates this).
  const headsetForPatient = (patientId) => headsets.find((h) => h.patient_id === patientId);

  const openCreate = () => { setEditing(null); setForm(defaultForm); setOpen(true); };
  const openEdit = (p) => {
    const linked = headsetForPatient(p.id);
    setEditing(p);
    setForm({ name: p.name || "", notes: p.notes || "", headset_id: linked?.id || "" });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) {
        const body = { name: form.name, notes: form.notes };
        const currentHeadset = headsetForPatient(editing.id);
        const currentId = currentHeadset?.id || "";
        if (form.headset_id !== currentId) {
          if (form.headset_id) body.headset_id = form.headset_id;
          else body.clear_headset = true;
        }
        await api.patch(`/patients/${editing.id}`, body);
        toast.success("Patient updated");
      } else {
        await api.post("/patients", { ...form, headset_id: form.headset_id || null });
        toast.success("Patient added");
      }
      setOpen(false);
      setEditing(null);
      setForm(defaultForm);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    const ok = await confirmDialog({
      title: "Remove patient?",
      description: `"${p.name}" will be archived (soft-deleted). Their telemetry history and audit log are retained for compliance.`,
      confirmText: "Archive",
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/patients/${p.id}`); toast.success("Patient archived"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const quickAssignHeadset = async (patientId, headsetId) => {
    try {
      await api.patch(`/patients/${patientId}`, { headset_id: headsetId });
      toast.success("Headset linked");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const usedHeadsetIds = new Set(headsets.filter((h) => h.patient_id).map((h) => h.id));
  const availableHeadsetsForCreate = headsets.filter((h) => !usedHeadsetIds.has(h.id));
  const totalHeadsets = headsets.length;
  const inUse = headsets.filter((h) => h.patient_id).length;

  return (
    <div>
      <PageHeader
        eyebrow="My Clinical Roster"
        title="Patients"
        description="Patients you manage. Click a row to access their headset, simulations, and usage data."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(defaultForm); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-patient-button" onClick={openCreate} className="btn-brand rounded-full">
                <Plus className="h-4 w-4 mr-2" /> New Patient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display">{editing ? "Edit patient" : "New patient"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Patient name / identifier</Label>
                  <Input
                    data-testid="patient-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. P-001 / Jane D."
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Notes</Label>
                  <Textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="patient-notes-input"
                    placeholder="Diagnosis, recovery goals, contraindications…"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Assigned headset</Label>
                  <Select
                    value={form.headset_id || "none"}
                    onValueChange={(v) => setForm({ ...form, headset_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="patient-headset-select">
                      <SelectValue placeholder="No headset" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No headset</SelectItem>
                      {headsets
                        .filter((h) => !h.patient_id || (editing && h.patient_id === editing.id))
                        .map((h) => (
                          <SelectItem key={h.id} value={h.id}>{h.name || h.serial} · {h.model}</SelectItem>
                        ))}
                      {!editing && availableHeadsetsForCreate.length === 0 && (
                        <div className="px-2 py-1 text-xs text-slate-500">No headsets available</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); setForm(defaultForm); }}>Cancel</Button>
                <Button
                  data-testid="patient-submit-button"
                  onClick={submit}
                  disabled={!form.name.trim()}
                  className="btn-brand rounded-full"
                >
                  {editing ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Active Patients", value: patients.length },
          { label: "Headsets Assigned to me", value: totalHeadsets },
          { label: "Headsets In Use", value: inUse },
        ].map((k) => (
          <div key={k.label} className="panel rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700 mb-2">{k.label}</div>
            <div className="font-display text-3xl font-bold tracking-tight text-slate-900 tabular-nums">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="panel rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-purple-50/50">
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Patient</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Headset</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Last sync</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Notes</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-16">
                  <Stethoscope className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">No patients yet</div>
                </TableCell>
              </TableRow>
            ) : patients.map((p) => {
              const headset = headsetForPatient(p.id);
              const headsetOptions = headsets.filter((h) => !h.patient_id || h.patient_id === p.id);
              return (
                <TableRow key={p.id} data-testid={`patient-row-${p.id}`} className="hover:bg-purple-50/30">
                  <TableCell>
                    <div className="font-medium text-slate-900">{p.name}</div>
                  </TableCell>
                  <TableCell>
                    {headset ? (
                      <Badge variant="outline" className="font-mono text-[10px] chip-brand border-0">{headset.name || headset.serial}</Badge>
                    ) : (
                      <Select
                        value="none"
                        onValueChange={(v) => quickAssignHeadset(p.id, v)}
                      >
                        <SelectTrigger
                          data-testid={`quick-assign-headset-${p.id}`}
                          className="h-8 text-xs min-w-[180px] border-purple-200"
                        >
                          <SelectValue placeholder="Assign headset…" />
                        </SelectTrigger>
                        <SelectContent>
                          {headsetOptions.length === 0 ? (
                            <div className="px-2 py-1 text-xs text-slate-500">No free headsets</div>
                          ) : headsetOptions.map((h) => (
                            <SelectItem key={h.id} value={h.id}>
                              <span className="inline-flex items-center gap-2">
                                <Link2 className="h-3 w-3" /> {h.name || h.serial} · {h.model}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 tabular-nums">
                    {headset?.last_seen ? new Date(headset.last_seen).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-xs truncate">{p.notes || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/patients/${p.id}`)}
                        data-testid={`patient-settings-${p.id}`}
                        className="mr-1 border-purple-200 hover:bg-purple-50"
                      >
                        Settings
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(p)}
                        data-testid={`patient-edit-${p.id}`}
                        title="Edit patient"
                      >
                        <Pencil className="h-4 w-4 text-slate-400 hover:text-purple-700" strokeWidth={1.5} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(p)}
                        data-testid={`patient-delete-${p.id}`}
                        title="Remove patient"
                      >
                        <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" strokeWidth={1.5} />
                      </Button>
                      {headset && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/headsets/${headset.id}`)}
                          data-testid={`patient-view-${p.id}`}
                          className="ml-1 border-purple-200 hover:bg-purple-50"
                        >
                          Open <ArrowUpRight className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
